// ---------------------------------------------------------------------------
// DatasetUsageSection — read-only per-dataset usage dashboard rendered below the
// metadata on the Dataset Overview tab.
//
// Shows headline KPI cards, a zero-filled weekly usage trend, category and
// application breakdown bars, and a per-bucket usage table for the last 30 days
// (the usage API's default range, at daily granularity). The usage API is
// available to any caller with at least read access (and to public datasets),
// so the fetch always runs and the API is the authority on access — a residual
// `403` is surfaced through the error state.
// ---------------------------------------------------------------------------

import {
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { DataUsage20Regular } from '@fluentui/react-icons';
import { useMemo } from 'react';

import type { DatasetUsageSectionProps } from './DatasetUsageSection.interface.js';
import { useStyles } from './DatasetUsageSection.style.js';
import { UsageBreakdown } from './UsageBreakdown.js';
import { UsageKpiCards } from './UsageKpiCards.js';
import { UsageTable } from './UsageTable.js';
import {
  breakdownByApplication,
  breakdownByCategory,
  summarizeUsage,
  toWeeklyTotals,
} from './usageUtils.js';
import { UsageWeeklyTrend } from './UsageWeeklyTrend.js';
import { useDatasetUsage } from './useDatasetUsage.js';

const SECTION_TITLE = 'Usage (Last 30 days)';
const CATEGORY_BREAKDOWN_TITLE = 'By Category';
const APPLICATION_BREAKDOWN_TITLE = 'By Application';
const EMPTY_MESSAGE = 'No usage has been recorded in the last 30 days.';
const LOADING_LABEL = 'Loading usage…';
const ERROR_PREFIX = 'Failed to load usage: ';

export function DatasetUsageSection({ datasetId }: DatasetUsageSectionProps) {
  const styles = useStyles();
  const { records, isLoading, error } = useDatasetUsage(datasetId);

  const summary = useMemo(() => summarizeUsage(records), [records]);
  const weeklyTotals = useMemo(() => toWeeklyTotals(records), [records]);
  const categoryItems = useMemo(() => breakdownByCategory(records), [records]);
  const applicationItems = useMemo(
    () => breakdownByApplication(records),
    [records],
  );

  function renderBody() {
    if (isLoading) {
      return (
        <div className={styles.stateWrap}>
          <Spinner size="small" label={LOADING_LABEL} labelPosition="after" />
        </div>
      );
    }

    if (error !== null) {
      return (
        <MessageBar intent="error" className={styles.stateWrap}>
          <MessageBarBody>{`${ERROR_PREFIX}${error.message}`}</MessageBarBody>
        </MessageBar>
      );
    }

    if (records.length === 0) {
      return (
        <div className={styles.stateWrap}>
          <Text className={styles.emptyState}>{EMPTY_MESSAGE}</Text>
        </div>
      );
    }

    return (
      <>
        <UsageKpiCards summary={summary} />
        <UsageWeeklyTrend weeks={weeklyTotals} />
        <div className={styles.breakdownGrid}>
          <UsageBreakdown
            title={CATEGORY_BREAKDOWN_TITLE}
            items={categoryItems}
          />
          <UsageBreakdown
            title={APPLICATION_BREAKDOWN_TITLE}
            items={applicationItems}
          />
        </div>
        <UsageTable records={records} />
      </>
    );
  }

  return (
    <div className={styles.root}>
      <Divider className={styles.divider} />
      <div className={styles.sectionTitle}>
        <DataUsage20Regular />
        <Text className={styles.sectionTitleText}>{SECTION_TITLE}</Text>
      </div>
      {renderBody()}
    </div>
  );
}
